import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useParams, useLocation } from 'react-router';
import ClubReadinessBanner from './components/ClubReadinessBanner';
import { identify, resetIdentity, trackPageView } from './lib/analytics';
import {
  LayoutDashboard, Calendar, DollarSign, IdCard, LogOut, User, Building2, Briefcase, Users, Rows3, Clock, Star, Package2, CalendarClock, MapPin, GraduationCap, Tag,
} from 'lucide-react';
import supabase from './lib/supabase';
import Login from './pages/Login';
import CoachDashboard from './pages/CoachDashboard';
import CoachLessons from './pages/CoachLessons';
import CoachRates from './pages/CoachRates';
import CoachProfile from './pages/CoachProfile';
import CoachStripe from './pages/CoachStripe';
import ClubOverview from './pages/ClubOverview';
import ClubCoaches from './pages/ClubCoaches';
import ClubCourts from './pages/ClubCourts';
import ClubVenues from './pages/ClubVenues';
import ClubPackages from './pages/ClubPackages';
import ClubPromos from './pages/ClubPromos';
import Account from './pages/Account';
import ClubSchedule from './pages/ClubSchedule';
import ClubBookings from './pages/ClubBookings';
import ClubRentals from './pages/ClubRentals';
import ClubStaff from './pages/ClubStaff';
import CoachMyClubs from './pages/CoachMyClubs';
import CoachReviews from './pages/CoachReviews';
import CoachStudents from './pages/CoachStudents';
import CoachTimeOff from './pages/CoachTimeOff';

type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];

type ClubRef = { id: string; name: string };

type Roles = {
  coachId: string | null;
  coachType: 'freelance' | 'staff' | null;
  clubs: ClubRef[];
  email: string;
};

const ACTIVE_CLUB_KEY = 'acerent.partner.activeClubId';

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<Roles | null>(null);
  const [activeClubId, setActiveClubIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingCoachRequests, setPendingCoachRequests] = useState(0);

  const setActiveClubId = (id: string | null) => {
    setActiveClubIdState(id);
    if (id) localStorage.setItem(ACTIVE_CLUB_KEY, id);
    else localStorage.removeItem(ACTIVE_CLUB_KEY);
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) {
        identify(data.session.user.id, { email: data.session.user.email });
        await loadRoles(data.session.user.id, data.session.user.email ?? '');
      }
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      setSession(sess);
      if (sess) {
        identify(sess.user.id, { email: sess.user.email });
        await loadRoles(sess.user.id, sess.user.email ?? '');
      } else {
        resetIdentity();
        setRoles(null);
        setActiveClubId(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Refresh the pending-freelance-coach-requests count whenever the user
  // switches clubs. Used to power the sidebar badge on "Coaches".
  useEffect(() => {
    if (!activeClubId) { setPendingCoachRequests(0); return; }
    let cancelled = false;
    (async () => {
      const { count } = await supabase
        .from('coach_affiliations')
        .select('id', { count: 'exact', head: true })
        .eq('club_id', activeClubId)
        .eq('status', 'pending');
      if (!cancelled) setPendingCoachRequests(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [activeClubId]);

  const loadRoles = async (userId: string, email: string) => {
    const [coachRes, memberRes] = await Promise.all([
      supabase.from('coaches').select('id, type').eq('user_id', userId).maybeSingle(),
      supabase
        .from('club_members')
        .select('club_id, clubs(id, name)')
        .eq('user_id', userId),
    ]);
    const clubs: ClubRef[] = ((memberRes.data ?? []) as any[])
      .map((r) => r.clubs ? { id: r.clubs.id, name: r.clubs.name } : null)
      .filter((c): c is ClubRef => !!c);
    setRoles({
      coachId: coachRes.data?.id ?? null,
      coachType: (coachRes.data?.type as 'freelance' | 'staff' | null) ?? null,
      clubs,
      email,
    });
    // Restore / default the active club.
    const saved = localStorage.getItem(ACTIVE_CLUB_KEY);
    const initial = saved && clubs.some((c) => c.id === saved)
      ? saved
      : clubs[0]?.id ?? null;
    setActiveClubIdState(initial);
  };

  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (!session) return <Login />;

  const noRole = !roles?.coachId && (roles?.clubs.length ?? 0) === 0;

  if (noRole) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-xl bg-[#c6f806]/20 mx-auto mb-4 flex items-center justify-center">
            <Briefcase className="w-7 h-7 text-[#2d5016]" />
          </div>
          <h1 className="text-xl mb-2">Not a partner yet</h1>
          <p className="text-sm text-gray-600 mb-4">
            This sign-in ({roles?.email}) isn't registered as a coach or club staff on
            AceRent. Contact ops@acerent.ae to get set up, or sign out and use a different
            account.
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="h-10 px-5 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen grid" style={{ gridTemplateColumns: '240px 1fr' }}>
        <aside className="bg-[#1a3010] text-white flex flex-col p-4 gap-1">
          <div className="px-3 py-4 flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#c6f806] flex items-center justify-center">
              <span className="text-[#1a3010] font-bold">A</span>
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">AceRent</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Partner</div>
            </div>
          </div>

          {roles?.coachId && (
            <>
              <div className="px-3 mt-2 mb-1 text-[10px] uppercase tracking-widest text-white/40">
                Coach
              </div>
              <NavItem to="/" icon={LayoutDashboard} label="Dashboard" end />
              <NavItem to="/lessons" icon={Calendar} label="Lessons" />
              <NavItem to="/students" icon={Users} label="My students" />
              <NavItem to="/rates" icon={DollarSign} label="Rates & packages" />
              <NavItem to="/time-off" icon={CalendarClock} label="Time off" />
              <NavItem to="/reviews" icon={Star} label="Reviews" />
              {roles.coachType === 'freelance' && (
                <NavItem to="/my-clubs" icon={Building2} label="My clubs" />
              )}
              <NavItem to="/profile" icon={User} label="Profile" />
              <NavItem to="/stripe" icon={IdCard} label="Stripe payouts" />
            </>
          )}

          {roles && roles.clubs.length > 0 && (
            <>
              <div className="px-3 mt-4 mb-1 text-[10px] uppercase tracking-widest text-white/40">
                Club
              </div>
              {roles.clubs.length > 1 && (
                <div className="px-3 py-1">
                  <label className="text-[10px] text-white/50 uppercase tracking-widest mb-1 block">
                    Manage
                  </label>
                  <select
                    value={activeClubId ?? ''}
                    onChange={(e) => setActiveClubId(e.target.value)}
                    className="w-full h-9 px-2 rounded-md bg-white/10 text-white text-sm border border-white/10 focus:outline-none focus:border-white/30"
                  >
                    {roles.clubs.map((c) => (
                      <option key={c.id} value={c.id} className="text-[#1a3010]">
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {roles.clubs.length === 1 && (
                <div className="px-3 py-1 text-xs text-white/70 truncate">
                  {roles.clubs[0].name}
                </div>
              )}
              <NavItem to="/club" icon={Building2} label="Overview" end />
              <NavItem to="/club/bookings" icon={Calendar} label="Bookings" />
              <NavItem to="/club/venues" icon={MapPin} label="Venues" />
              <NavItem to="/club/courts" icon={Rows3} label="Courts" />
              <NavItem to="/club/schedule" icon={Clock} label="Schedule" />
              <NavItem to="/club/rentals" icon={Package2} label="Rentals" />
              <NavItem to="/club/packages" icon={GraduationCap} label="Packages" />
              <NavItem to="/club/promos" icon={Tag} label="Promotions" />
              <NavItem
                to="/club/coaches"
                icon={Users}
                label="Coaches"
                badge={pendingCoachRequests > 0 ? pendingCoachRequests : undefined}
              />
              <NavItem to="/club/team" icon={User} label="Team" />
            </>
          )}

          <div className="mt-auto px-3 pt-2 border-t border-white/10">
            <NavItem to="/account" icon={User} label="Account" />
            <div className="px-3 py-1 text-xs text-white/60 truncate">{roles?.email}</div>
          </div>
          <button
            onClick={() => supabase.auth.signOut()}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg text-white/80 hover:bg-white/10"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </aside>

        <main className="min-h-screen bg-[#f6f7f4]">
          <SentrySmokeTest />
          <RouteTracker />
          <ClubReadinessOnRouteChange clubId={activeClubId} />
          <Routes>
            {roles?.coachId && (
              <>
                <Route path="/" element={<CoachDashboard coachId={roles.coachId} />} />
                <Route path="/lessons" element={<CoachLessons coachId={roles.coachId} />} />
                <Route path="/rates" element={<CoachRates coachId={roles.coachId} />} />
                <Route path="/profile" element={<CoachProfile coachId={roles.coachId} />} />
                <Route path="/stripe" element={<CoachStripe coachId={roles.coachId} />} />
                <Route path="/reviews" element={<CoachReviews coachId={roles.coachId} />} />
                <Route path="/students" element={<CoachStudents coachId={roles.coachId} />} />
                <Route path="/time-off" element={<CoachTimeOff coachId={roles.coachId} />} />
                {roles.coachType === 'freelance' && (
                  <Route path="/my-clubs" element={<CoachMyClubs coachId={roles.coachId} />} />
                )}
              </>
            )}
            {activeClubId && (
              <>
                <Route path="/club" element={<ClubOverview clubId={activeClubId} />} />
                <Route path="/club/bookings" element={<ClubBookings clubId={activeClubId} />} />
                <Route path="/club/venues" element={<ClubVenues clubId={activeClubId} />} />
                <Route path="/club/courts" element={<ClubCourts clubId={activeClubId} />} />
                <Route path="/club/schedule" element={<ClubSchedule clubId={activeClubId} />} />
                <Route path="/club/rentals" element={<ClubRentals clubId={activeClubId} />} />
                <Route path="/club/packages" element={<ClubPackages clubId={activeClubId} />} />
                <Route path="/club/promos" element={<ClubPromos clubId={activeClubId} />} />
                <Route path="/club/coaches" element={<ClubCoaches clubId={activeClubId} />} />
                <Route path="/club/coaches/:coachId/profile" element={<CoachProfileByParam />} />
                <Route path="/club/team" element={<ClubStaff clubId={activeClubId} />} />
              </>
            )}
            <Route path="/account" element={<Account />} />
            <Route path="*" element={<Navigate to={roles?.coachId ? '/' : '/club'} replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

/**
 * Guarded Sentry smoke-test button. Only renders when the URL carries
 * ?sentry-test=1. Sits at the top of the main content area on every
 * partner page so club-staff or coaches can trigger a crash from
 * whichever page they land on after login.
 */
function SentrySmokeTest() {
  if (typeof window === 'undefined') return null;
  const show = new URLSearchParams(window.location.search).get('sentry-test') === '1';
  if (!show) return null;
  return (
    <div className="p-4 border-b border-amber-200 bg-amber-50">
      <div className="max-w-5xl mx-auto flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-900">Sentry smoke test</div>
          <p className="text-xs text-amber-900/80">
            Visible only with ?sentry-test=1 in the URL.
          </p>
        </div>
        <button
          onClick={() => { throw new Error('Sentry smoke test at ' + new Date().toISOString()); }}
          className="h-9 px-3 rounded-lg bg-red-600 text-white text-xs"
        >
          Break the world
        </button>
      </div>
    </div>
  );
}

function NavItem({
  to, icon: Icon, label, end, badge,
}: { to: string; icon: any; label: string; end?: boolean; badge?: number | string }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${
          isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {badge !== undefined && (
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#c6f806] text-[#1a3010] text-[10px] font-semibold">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Thin wrapper so a club owner/manager can open any of their staff
 * coaches' profiles at /club/coaches/:coachId/profile. RLS on the
 * coaches table already enforces that only a manager of the coach's
 * home club can write — no extra authZ gate here.
 */
function CoachProfileByParam() {
  const { coachId } = useParams<{ coachId: string }>();
  if (!coachId) return <Navigate to="/club/coaches" replace />;
  return <CoachProfile coachId={coachId} />;
}

/**
 * Renders the club-readiness banner only on /club/* routes — the
 * coach-side pages don't have a "club setup" concept, so showing the
 * banner there would be confusing.
 */
function ClubReadinessOnRouteChange({ clubId }: { clubId: string | null }) {
  const loc = useLocation();
  if (!clubId || !loc.pathname.startsWith('/club')) return null;
  return <ClubReadinessBanner clubId={clubId} />;
}

/**
 * Fires a PostHog $pageview every time the route changes. The screen
 * name is derived from the path's first two segments so URLs with
 * dynamic ids (e.g. /club/coaches/<uuid>/profile) don't fan out
 * into thousands of unique pages in the analytics dashboard.
 */
function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    const parts = loc.pathname.split('/').filter(Boolean);
    const screen = parts.length === 0 ? 'root' : parts.slice(0, 2).join('/');
    trackPageView(screen, { path: loc.pathname });
  }, [loc.pathname]);
  return null;
}
